import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { Icon, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useScreenClass } from 'react-grid-system';
import styled from 'styled-components';
import theme from '../styles/theme';
import { CustomBlock, StyledImg } from './CompetenciesList';
import CompetencyModal from './CompetencyModal';
import FlexBox, { FlexGrow } from './FlexBox';
import { ClickableIcon } from './Icon';
import LoadingSpinner from './LoadingSpinner';
import TabGroup, { TabItem } from './TabGroup';
import { StyledText, TextSize, TextStatus } from './Text';

const Header = styled.h4`
  font-family: Montserrat;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0px;
  text-align: left;
`;
const CustomFlex = styled(FlexBox)`
  padding: 20px 0;
  align-items: center;
  border-bottom: 1px solid #3c406c;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px 0;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 150px;
  flex-grow: 0;
`;
const CustomGrow = styled(FlexGrow)`
  flex-grow: 0;
  height: 100%;
  border-right: 1px solid #3c406c;
`;
const Label = styled.h5`
  font-family: Montserrat;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
const LabelData = styled(StyledText)`
  font-family: Montserrat;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  color: #e6e6e6;
  vertical-align: text-top;
`;
const Ul = styled.ul`
  color: #d0d0d0;
  padding-inline-start: 16px;
  margin-block-start: 0;
  margin-block-end: 0;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
type StudentHistoryModelProps = {
  isOpen?: boolean;
  data: any;
  semesterStart: string;
  semesterEnd: string;
  setOpen: (x: boolean) => void;
};
const StudentHistoryModel = ({
  isOpen,
  setOpen,
  data,
  semesterEnd,
  semesterStart,
}: StudentHistoryModelProps): JSX.Element => {
  const screenClass = useScreenClass();
  const isMobile = useMemo(
    () => ['xs', 'sm'].includes(screenClass),
    [screenClass],
  );
  const isTablet = useMemo(
    () => ['md'].includes(screenClass),
    [screenClass],
  );
  const [historyData, setHistoryData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    if (data && data.id) {
      fetchHistory(data.id, semesterStart, semesterEnd);
    }
  }, [data, semesterEnd, semesterStart]);
  const fetchHistory = (
    studentId: string,
    semesterStart: string,
    semesterEnd: string,
  ) => {
    setLoading(true);
    axios
      .get(
        `/student/history?student_id=${studentId}&start_date=${semesterStart}&end_date=${semesterEnd}`,
      )
      .then((res) => {
        if (res.data.history_event) {
          setHistoryData(res.data.history_event);
        }
        setLoading(false);
      })
      .catch(function (error) {
        setHistoryData([]);
        setLoading(false);
      });
  };
  return (
    <>
      <CompetencyModal
        customStyle={{
          overlay: {
            zIndex: '1005',
          },
          content: {
            inset: `0px 0px 0px ${isMobile ? '140px' : isTablet ? '340px' :'700px'}`,
            maxWidth: '100%',
            background: '#0B0C1F',
          },
        }}
        isOpen={isOpen || false}
        setOpen={setOpen}>
        <CustomFlex>
          <FlexGrow>
            <Header>History for {data?.nickname}</Header>
          </FlexGrow>
          <ClickableIcon
            size={3.125}
            fixedWidth={false}
            name="times"
            onClick={() => setOpen(false)}
          />
        </CustomFlex>
        <CustomFlex
          className="visible-scrollbar"
          style={{
            borderBottom: 'none',
            height: '85vh',
            alignItems: 'flex-start',
            overflowY: 'scroll',
          }}>
          <FlexGrow>
            <Timeline>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  {historyData.length === 0 ? (
                    <StyledText
                      style={{
                        color: 'gray',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                      }}>
                      No history data
                    </StyledText>
                  ) : (
                    <>
                      {historyData.map((history: any) => (
                        <TimelineItem
                          sx={{ ':before': { flex: 0, padding: 0 } }}>
                          <TimelineSeparator>
                            <TimelineDot sx={{ backgroundColor: '#C54B4D' }} />
                            <TimelineConnector
                              sx={{ backgroundColor: '#3C406C' }}
                            />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography color={'#9295AA'} component="span">
                              {history.date_time}
                            </Typography>
                            <Typography fontWeight={'bold'}>
                              {history.event_type}
                            </Typography>
                            <Typography fontWeight={'300'}>
                              {history.event_description}
                            </Typography>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </>
                  )}
                </>
              )}
            </Timeline>
          </FlexGrow>
        </CustomFlex>
      </CompetencyModal>
    </>
  );
};

export default StudentHistoryModel;
